import { tabById, tabSections, SECTION_NUMBERS } from '../schema/pqTabs.js';
import FormSection from '../components/FormSection.jsx';
import PQDashboard from '../components/PQDashboard.jsx';
import TabBar, { TabFooter } from '../components/TabBar.jsx';
import NotesDock from '../components/NotesDock.jsx';
import { useLayout } from '../store/LayoutContext.jsx';

// The dock's own chrome, matched in CSS: the collapsed bar is the 38px head
// alone; open adds the 6px resize grip above it and the textarea's 12px bottom
// margin below. The shell pads its bottom by the dock's total height so the
// last section can always scroll clear of it.
const DOCK_BAR_H = 38;
const DOCK_OPEN_CHROME_H = 6 + 38 + 12;

// One page of the questionnaire at a time (schema/pqTabs.js): the tab bar
// pinned under the topbar, that tab's sections below it, the insights rail
// beside them, and the notes docked along the bottom. Only the open tab's
// sections are mounted, so the form is never longer than a page.
//
// The tab bar sits outside the grid on purpose. A sticky element can only
// move inside its containing block, and a grid item's containing block is its
// own grid area, one bar tall, so inside the grid it would never stick.
//
// Zoom mode (a half-width window beside a Zoom or Teams call) is the same
// screen without the rail: the tab bar carries the rail's three figures
// instead, and the topbar shrinks to what a live meeting needs.
export default function PQScreen() {
  const {
    activeTab, railCollapsed, railHidden,
    zoomMode, notesOpen, notesHeight,
  } = useLayout();

  const tab = tabById(activeTab);
  const sections = tabSections(tab).map(s => (
    <FormSection key={s.id} section={s} num={SECTION_NUMBERS[s.id]} />
  ));

  const dockH = notesOpen ? notesHeight + DOCK_OPEN_CHROME_H : DOCK_BAR_H;
  const zoom = zoomMode ? ' zoom-mode' : '';
  const layoutClass =
    'layout' + zoom +
    (!zoomMode && railCollapsed ? ' rail-collapsed' : '') +
    (!zoomMode && railHidden ? ' rail-hidden' : '');

  return (
    <form
      id="pq-form"
      autoComplete="off"
      onSubmit={e => e.preventDefault()}
      className={'pq-shell' + zoom}
      style={{ '--dock-h': `${dockH}px` }}
    >
      <TabBar />
      <div className={layoutClass}>
        {/* Keyed by tab so a page change is a fresh panel: no section state
            (open detail rows, a focused cell) carries from one page to another. */}
        <main
          key={tab.id}
          className="main"
          role="tabpanel"
          id={`tabpanel-${tab.id}`}
          aria-labelledby={`tab-${tab.id}`}
        >
          {sections}
          <TabFooter tabId={tab.id} />
        </main>
        {!zoomMode && <PQDashboard />}
      </div>
      <NotesDock />
    </form>
  );
}
